/**
 * Add to Cart Actions
 */
/**
 * Internal dependencies
 */
import { productApiService, cartApiService } from "../../services/api";
import { AddToCartValidator } from "../../services/validation/form-validator";
import { tracker } from "../../services/analytics";
import type { ProductVariant, Fee } from "../../services/api";
import type {
	AddToCartCommand,
	ValidationErrors,
} from "../../types/add-to-cart.types";
import type { ProductInstanceState } from "../../types/add-to-cart.types";
import type { Cart } from "../../types/cart.types";


import { ACTION_TYPES as types } from "./action-types";

// ── Synchronous action creators ───────────────────────────────────────────────

export function initializeProduct(
	productId: number,
	price: number,
	currency: string,
	formattedPrice: string,
	productType = "simple",
) {
	return {
		type: types.INITIALIZE_PRODUCT,
		currency,
		formattedPrice,
		price,
		productId,
		productType,
	} as const;
}

export function resetForm(productId: number) {
	return { type: types.RESET_FORM, productId } as const;
}

export function selectVariant(productId: number, variantId: number) {
	return { type: types.SELECT_VARIANT, productId, variantId } as const;
}

export function setAdding(productId: number, isAdding: boolean) {
	return { type: types.SET_ADDING, isAdding, productId } as const;
}

export function setError(productId: number, error: null | string) {
	return { type: types.SET_ERROR, error, productId } as const;
}

export function setFees(productId: number, fees: Fee[]) {
	return { type: types.SET_FEES, fees, productId } as const;
}

export function setLoadingVariants(productId: number, isLoading: boolean) {
	return { type: types.SET_LOADING_VARIANTS, isLoading, productId } as const;
}

export function setQuantity(productId: number, quantity: number) {
	return { type: types.SET_QUANTITY, productId, quantity } as const;
}

export function setSuccess(productId: number, message: null | string) {
	return { type: types.SET_SUCCESS, message, productId } as const;
}

export function setValidationErrors(
	productId: number,
	errors: ValidationErrors,
) {
	return { type: types.SET_VALIDATION_ERRORS, errors, productId } as const;
}

export function setVariants(productId: number, variants: ProductVariant[]) {
	return { type: types.SET_VARIANTS, productId, variants } as const;
}

// ── Internal dispatch / thunk context shapes ──────────────────────────────────
// Defined here (not in thunks.ts) so the ESLint TypeScript language service
// can see all exported names within a single module during PromisifiedActionCreators
// inference. `export * from './thunks'` causes a cross-file gap in that resolution.

type Dispatch = {
	initializeProduct: (
		productId: number,
		price: number,
		currency: string,
		formattedPrice: string,
		productType?: string,
	) => void;
	resetForm: (productId: number) => void;
	selectVariant: (productId: number, variantId: number) => void;
	setAdding: (productId: number, isAdding: boolean) => void;
	setError: (productId: number, error: null | string) => void;
	setFees: (productId: number, fees: Fee[]) => void;
	setLoadingVariants: (productId: number, isLoading: boolean) => void;
	setQuantity: (productId: number, quantity: number) => void;
	setSuccess: (productId: number, message: null | string) => void;
	setValidationErrors: (productId: number, errors: ValidationErrors) => void;
	setVariants: (productId: number, variants: ProductVariant[]) => void;
};

type Registry = {
	dispatch: (storeName: "ebs/cart") => { setCartData: (cart: Cart) => void };
};

type Select = {
	getInstance: (productId: number) => ProductInstanceState | undefined;
};

type ThunkContext = { dispatch: Dispatch; registry: Registry; select: Select };

// ── Thunks ────────────────────────────────────────────────────────────────────

async function runLoadVariants(
	productId: number,
	{ dispatch }: { dispatch: Dispatch },
) {
	try {
		dispatch.setLoadingVariants(productId, true);
		const [variants, product] = await Promise.all([
			productApiService.getProductVariants(productId),
			productApiService.getProduct(productId),
		]);
		dispatch.setVariants(productId, variants);
		dispatch.setFees(productId, product.fees);
		dispatch.setError(productId, null);
	} catch (error) {
		const message =
			error instanceof Error ? error.message : "Failed to load variants";
		dispatch.setError(productId, message);
	} finally {
		dispatch.setLoadingVariants(productId, false);
	}
}

export const loadVariants =
	(productId: number) => (context: { dispatch: Dispatch }) =>
		runLoadVariants(productId, context);

async function runAddToCart(
	command: AddToCartCommand,
	{ dispatch, select, registry }: ThunkContext,
) {
	try {
		const instance = select.getInstance(command.productId);
		if (!instance) {
			throw new Error("Product instance not found");
		}

		const errors = AddToCartValidator.validateForm(
			command.quantity,
			command.variant_id ?? null,
			instance.variants,
			instance.productType,
		);

		if (!AddToCartValidator.isValid(errors)) {
			dispatch.setValidationErrors(command.productId, errors);
			throw new Error("Validation failed");
		}

		dispatch.setAdding(command.productId, true);

		const cart = await cartApiService.addItem(command);

		registry.dispatch("ebs/cart").setCartData(cart);

		const variant = instance.variants.find(
			(v: { id: number }) => v.id === command.variant_id,
		);
		const label = variant ? variant.name : "item";
		dispatch.setSuccess(command.productId, `${label} added to cart!`);
		dispatch.resetForm(command.productId);
		dispatch.setError(command.productId, null);

		const addedCartItem = cart.items.find(
			( i ) => i.product_id === command.productId && i.variant_id === command.variant_id,
		);
		const itemPrice = ( variant ? variant.price_amount : instance.price ) / 100;
		tracker.addToCart(
			instance.currency,
			itemPrice * command.quantity,
			[ {
				item_id: String( command.productId ),
				item_name: addedCartItem?.name ?? '',
				item_variant: variant?.name,
				price: itemPrice,
				quantity: command.quantity,
			} ],
		);
	} catch (error) {
		const message =
			error instanceof Error ? error.message : "Failed to add to cart";
		dispatch.setError(command.productId, message);
		throw error;
	} finally {
		dispatch.setAdding(command.productId, false);
	}
}

export const addToCart =
	(command: AddToCartCommand) => (context: ThunkContext) =>
		runAddToCart(command, context);
